<template>
  <div class="animated revealUp">
    <div class="white-wrapper">
      <div class="section-title">Online Booking Fee Report</div>
      <div class="mt-3 mb-2">
        <div class="row">
          <div class="col-lg-6 col-xl-3 align-self-center mb-3">
            <div class="dropdown-curve-container">
              <label
                :class="
                  dropDown.cinemas && dropDown.cinemas.length > 0
                    ? 'shift-up'
                    : ''
                "
                >{{ $tc("cinema", 1) }}</label
              >
              <multiselect
                :disabled="isRoleID"
                :options="dropDown.cinemas"
                v-model="dropDown.selectedCinema"
                :searchable="true"
                label="cine_name"
                track-by="cine_id"
                placeholder="Cinema"
                @input="onSelectCinema($event)"
                :allowEmpty="false"
                :showLabels="false"
              ></multiselect>
            </div>
          </div>
          <div class="col-lg-6 col-xl-2 align-self-center mb-3">
            <div class="input-material">
              <div class="add-datepicker">
                <button
                  class="btn-clearDate"
                  v-if="fromDate"
                  @click="fromDate = ''"
                >
                  ✖
                </button>
                <Datepicker
                  :monday-first="true"
                  type="date"
                  ref="programaticOpen"
                  v-model="fromDate"
                  v-validate="'required'"
                  @selected="onChangeDate($event, 'from')"
                ></Datepicker>
                <label :class="fromDate ? 'active' : ''">
                  {{ $t("fromdate") }}
                </label>
              </div>
            </div>
          </div>
          <div class="col-lg-6 col-xl-2 align-self-center mb-3">
            <div class="input-material">
              <div class="add-datepicker">
                <button
                  class="btn-clearDate"
                  v-if="toDate"
                  @click="toDate = ''"
                >
                  ✖
                </button>
                <Datepicker
                  :monday-first="true"
                  ref="programaticOpen"
                  v-model="toDate"
                  v-validate="'required'"
                ></Datepicker>
                <label :class="toDate ? 'active' : ''">{{
                  $t("todate")
                }}</label>
              </div>
            </div>
          </div>
          <div class="col-lg-6 col-xl-3 align-self-center mb-3">
            <div class="dropdown-curve-container">
              <label
                :class="
                  selectedScreen &&
                  (selectedScreen.screen_id ||
                    selectedScreen.screen_name === 'All')
                    ? 'shift-up'
                    : ''
                "
                >{{ $tc("screen", 1) }}</label
              >
              <multiselect
                :options="cineScreens"
                v-model="selectedScreen"
                :searchable="true"
                label="screen_name"
                track-by="screen_id"
                placeholder="Screen"
                :allowEmpty="false"
                :showLabels="false"
              ></multiselect>
            </div>
          </div>
          <div class="col-lg-6 col-xl-2 align-self-center mb-3">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="isSummary"
                class="j-checkbox mb-0"
              />
              <label class="mb-0 ml-2">Summary</label>
            </div>
          </div>
          <div class="col-lg-6 col-xl-2 align-self-center mb-3">
            <button
              class="btn-blue-lg w-100"
              @click.prevent="getOnlineBookingFeeReport('N', 'N')"
            >
              {{ $t("fetch") }}
            </button>
          </div>
          <div
            v-if="records.length > 0"
            class="mr-3 w-100 align-self-center mb-3 d-flex justify-content-end align-items-center"
          >
            <div class="mx-1">{{ $t("download") }} as:</div>
            <a
              target="_blank"
              @click="getOnlineBookingFeeReport('Y', 'N')"
              title="Download as Excel"
              download
              style="cursor: pointer"
            >
              <img
                class="mx-1"
                src="@/assets/img/icons/excel.svg"
                height="25"
                width="25"
              />
            </a>
            <a
              target="_blank"
              @click="getOnlineBookingFeeReport('N', 'Y')"
              title="Download as PDF"
              download
              style="cursor: pointer"
            >
              <img
                class="mx-1"
                src="@/assets/img/icons/pdf.svg"
                height="25"
                width="25"
              />
            </a>
          </div>
        </div>
      </div>

      <div
        style="overflow-x: scroll; width: auto !important"
        ref="exportable_table"
      >
        <table class="swc-report-table" border="1">
          <thead>
            <tr class="bg-lightgrey font-weight-bold">
              <th>Booking Code</th>
              <th>Payment Method</th>
              <th>Tickets Price (EGP)</th>
              <th>Glasses Rent (EGP)</th>
              <th>Purchase Price (EGP)</th>
              <th>Tickets</th>
              <th>Commission (EGP)</th>
              <th>Before VAT (EGP)</th>
              <th>VAT (EGP)</th>
              <th>Profit (EGP)</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="visibleRecords.length > 0">
              <tr
                v-for="(row, index) in visibleRecords"
                :key="'obf-' + (row.sb_id || index) + '-' + index"
                :class="{
                  'bg-lightgrey font-weight-bold': isTotalRow(row),
                }"
              >
                <td class="text-center">
                  {{ isTotalRow(row) ? "" : row.booking_code || "-" }}
                </td>
                <td class="text-center">
                  {{ isTotalRow(row) ? "TOTAL" : row.payment_mode }}
                </td>
                <td class="text-center">
                  {{ formatAmount(row.tickets_price) }}
                </td>
                <td class="text-center">
                  {{ formatAmount(row.glasses_rent) }}
                </td>
                <td class="text-center">
                  {{ formatAmount(row.purchase_price) }}
                </td>
                <td class="text-center">{{ row.tickets_count }}</td>
                <td class="text-center">
                  {{ formatAmount(row.commission) }}
                </td>
                <td class="text-center">
                  {{ formatAmount(row.before_vat) }}
                </td>
                <td class="text-center">{{ formatAmount(row.vat) }}</td>
                <td class="text-center">{{ formatAmount(row.profit) }}</td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="10">
                <NoRecords />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import MasterService from "@/services/MasterService";
import NewReportService from "@/services/NewReportService";
import CinemasService from "@/services/CinemasService";
import moment from "moment";
import NoRecords from "@/components/Partials/NoRecords.vue";

export default {
  components: {
    NoRecords,
  },
  data() {
    return {
      isRoleID: false,
      dropDown: {
        cinemas: [],
        selectedCinema: null,
        selectedCinemaName: null,
        selectedCinemaId: "",
        selectedCinemaAddress: "",
      },
      fromDate: moment().toDate(),
      toDate: new Date(),
      cineScreens: [{ screen_id: null, screen_name: "All" }],
      selectedScreen: { screen_id: null, screen_name: "All" },
      records: [],
      isSummary: false,
    };
  },
  computed: {
    visibleRecords() {
      if (!this.isSummary) return this.records;
      return this.records.filter((row) => this.isTotalRow(row));
    },
  },
  beforeMount() {
    let CinemaObj = this.$store.getters.getSelectedDropDownCinema;
    this.getCinemaList();
    if (CinemaObj) {
      this.onSelectCinema(CinemaObj);
    }
  },
  watch: {
    "$store.getters.getSelectedDropDownCinema": function (CinemaObj) {
      if (CinemaObj) {
        this.onSelectCinema(CinemaObj);
      }
    },
  },
  methods: {
    isTotalRow(row) {
      return (
        row &&
        (row.is_total === true ||
          String(row.payment_mode || "").toUpperCase() === "TOTAL")
      );
    },
    /** Show the real number. Cap long decimals at 6 and drop trailing zeros. */
    formatAmount(value, maxDigits = 6) {
      const num = Number(value || 0);
      if (!Number.isFinite(num)) return "0";
      return String(Number(num.toFixed(maxDigits)));
    },
    onChangeDate(e, type) {
      if (type == "from") {
        this.toDate = new Date(e);
        this.$forceUpdate();
      }
    },
    moment,
    getCinemaList() {
      MasterService.GetCinemasByRoles()
        .then((response) => {
          const { data } = response;
          if (data.status && data.Records.length > 0) {
            this.dropDown.cinemas = data.Records;
          } else {
            this.$toasted.show(data.message);
          }
        })
        .catch(this._CatchError);
    },
    async loadScreens(cinemaId) {
      this.cineScreens = [{ screen_id: null, screen_name: "All" }];
      this.selectedScreen = { screen_id: null, screen_name: "All" };
      if (!cinemaId) return;

      try {
        const { data: ScreenData } = await CinemasService.GetActiveScreenByCinema(
          {
            tableName: "screens",
            activekey: "screen_is_active",
            conditionColName: "cinema_id",
            conditionId: cinemaId,
            limit: 100,
            currentPage: 1,
            searchValue: "",
            searchKey: "screen_name",
            columnName: "",
            orderBy: "screen_id",
          }
        );
        if (ScreenData && ScreenData.status) {
          const screens = (ScreenData.Records || [])
            .filter((x) => x.screen_layout_active && x.screen_is_active)
            .sort((a, b) => a.screen_id - b.screen_id);
          this.cineScreens = [
            { screen_id: null, screen_name: "All" },
            ...screens,
          ];
        }
      } catch (e) {
        this.$toasted.show(e.message || "Please try later");
      }
    },
    onSelectCinema(event) {
      this.dropDown.selectedCinema = event;
      this.dropDown.selectedCinemaName = event.cine_name;
      this.dropDown.selectedCinemaId = event.cine_id;
      this.dropDown.selectedCinemaAddress = event.cine_address;
      this.$store.dispatch("selectDropdownCinema", event);
      this.loadScreens(event.cine_id);
    },
    getOnlineBookingFeeReport(isExcel = "N", isPdf = "N") {
      if (!this.dropDown.selectedCinemaId) {
        return this.$toasted.show("Select Cinema");
      }
      if (!this.fromDate || !this.toDate) {
        return this.$toasted.show("Select From and To date");
      }

      if (isExcel !== "Y" && isPdf !== "Y") {
        this.records = [];
      }

      let loader = this.$loading.show();
      let payload = {
        cinema_id: this.dropDown.selectedCinemaId,
        cinema_name: this.dropDown.selectedCinema
          ? this.dropDown.selectedCinema.cine_name
          : "",
        cinema_address: this.dropDown.selectedCinemaAddress,
        fromDate: moment(this.fromDate).format("YYYY-MM-DD"),
        toDate: moment(this.toDate).format("YYYY-MM-DD"),
        isReport: "Y",
        isExcel,
        isPdf,
        isSummary: this.isSummary ? "Y" : "N",
      };
      if (this.selectedScreen && this.selectedScreen.screen_id) {
        payload.screen_id = this.selectedScreen.screen_id;
      }

      NewReportService.GetOnlineBookingFeeReport(payload)
        .then((response) => {
          const { data } = response;
          if (data.status && data.Records && data.Records.length > 0) {
            let rows = data.FinalResult || data.Records || [];
            const hasTotal = rows.some((r) => this.isTotalRow(r));
            if (!hasTotal && data.Totals) {
              rows = [
                ...rows.filter((r) => !this.isTotalRow(r)),
                { ...data.Totals, is_total: true, payment_mode: "TOTAL" },
              ];
            }
            this.records = rows;
            this.$toasted.show(data.message);
            if (isExcel == "Y" && data.excelPath) {
              window.open(data.excelPath, "_blank");
            }
            if (isPdf == "Y" && data.pdfPath) {
              window.open(data.pdfPath, "_blank");
            }
          } else {
            if (isExcel !== "Y" && isPdf !== "Y") {
              this.records = [];
            }
            this.$toasted.show(data.message || "No Records found");
          }
          loader.hide();
        })
        .catch(this._CatchError)
        .finally(() => {
          setTimeout(() => {
            loader.hide();
          }, 100);
        });
    },
  },
};
</script>

<style>
.swc-report-table {
  table-layout: auto !important;
  overflow-x: scroll !important;
  border: 1px solid black;
}
.swc-report-table td {
  white-space: nowrap;
}
.swc-report-table th {
  border: 1px solid black !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Pages/ReportsContainer/onlineBookingFeeReport.vue